import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Infinity,
  Check,
  EyeOff,
  UtensilsCrossed,
  FileSpreadsheet,
  Palette,
  Mail,
  Users,
  Trash2,
  X,
  ChevronDown,
  ArrowRight,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { displayFont, fadeInUp, staggerContainer } from './shared';
import type { HomepageStrings } from '../../i18n/homepage';

type WeddingPlannerStrings = Pick<
  HomepageStrings,
  | 'wedding_planner_h2_pre'
  | 'wedding_planner_h2_highlight'
  | 'wedding_planner_h2_post'
  | 'wedding_planner_body'
  | 'wedding_planner_features'
  | 'wedding_planner_cta'
>;

const EN_DEFAULTS: WeddingPlannerStrings = {
  wedding_planner_h2_pre: 'Are you a ',
  wedding_planner_h2_highlight: 'wedding planner',
  wedding_planner_h2_post: ' or venue?',
  wedding_planner_body: 'Manage up to 10 active invites with professional-grade tools. One simple price, no hidden costs.',
  wedding_planner_features: [
    { title: 'Up to 10 Active Invites', description: '£29/month subscription. Manage up to 10 active invites, unlimited guests. No per-guest fees.' },
    { title: 'Bulk Operations', description: 'Select multiple guests and perform actions in one click — send emails, change RSVPs, or reassign invites.' },
    { title: 'Show & Hide by Tag', description: 'Control which sections each guest group sees. Evening guests skip ceremony details.' },
    { title: 'Dietary Requirements', description: 'Collect dietary needs as part of the RSVP — all tracked automatically in your dashboard.' },
    { title: 'CSV Import & Export', description: 'Import guest lists from spreadsheets or export all your data anytime.' },
    { title: 'Full Customization', description: 'Choose from 32 font pairings and dozens of color palettes. Brand every invite perfectly.' },
  ],
  wedding_planner_cta: 'Get Started',
};

const PLANNER_FEATURE_VISUALS: ReactNode[] = [
  // Up to 10 Active Invites
  (
    <div className="w-full max-w-[240px] mx-auto grid grid-cols-3 gap-1.5">
      <div className="bg-white/5 rounded-lg border border-white/10 p-2">
        <div className="text-[18px] font-bold text-white">£29</div>
        <div className="text-[9px] text-primary font-medium">per month</div>
      </div>
      <div className="bg-white/5 rounded-lg border border-white/10 p-2">
        <div className="text-[18px] font-bold text-white flex items-center gap-0.5">
          <Infinity className="w-3.5 h-3.5" />
        </div>
        <div className="text-[9px] text-white/50 font-medium">weddings</div>
      </div>
      <div className="bg-white/5 rounded-lg border border-white/10 p-2">
        <div className="text-[18px] font-bold text-white flex items-center gap-0.5">
          <Infinity className="w-3.5 h-3.5" />
        </div>
        <div className="text-[9px] text-white/50 font-medium">guests</div>
      </div>
    </div>
  ),
  // Bulk Operations
  (
    <div className="w-full max-w-[240px] mx-auto">
      <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a1a] text-white rounded-lg shadow-lg border border-white/10">
        <div className="flex items-center gap-1.5 text-[10px] font-medium">
          <Check size={12} />
          <span>3 selected</span>
        </div>
        <div className="w-px h-4 bg-white/20" />
        <div className="flex items-center gap-0.5">
          <button className="p-1.5 rounded hover:bg-white/10 transition-colors" title="Send Email">
            <Mail size={12} />
          </button>
          <button className="p-1.5 rounded hover:bg-white/10 transition-colors" title="Change RSVP">
            <Users size={12} />
          </button>
          <button className="p-1.5 rounded hover:bg-white/10 transition-colors text-red-400" title="Remove">
            <Trash2 size={12} />
          </button>
        </div>
        <div className="w-px h-4 bg-white/20" />
        <button className="p-1 rounded hover:bg-white/10 transition-colors" aria-label="Dismiss">
          <X size={12} />
        </button>
      </div>
    </div>
  ),
  // Show & Hide by Tag
  (
    <div className="w-full max-w-[240px] mx-auto bg-white/5 rounded-lg border border-white/10 shadow-sm overflow-hidden">
      <div className="px-3.5 py-2 border-b border-white/10">
        <span className="text-[10px] text-white/70 font-medium">Visible to</span>
      </div>
      <div className="px-3 py-2.5 flex flex-wrap gap-1.5">
        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-500/15 text-sky-300">Day Guest</span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/15 text-purple-300">Family</span>
      </div>
    </div>
  ),
  // Dietary Requirements
  (
    <div className="w-full max-w-[240px] mx-auto bg-white/5 rounded-lg border border-white/10 shadow-sm overflow-hidden px-3 py-3">
      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-primary/20 text-primary">
          <Check size={10} />
          Vegetarian
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white/10 text-white/60">
          Vegan
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white/10 text-white/60">
          Pescatarian
        </span>
      </div>
    </div>
  ),
  // CSV Import & Export
  (
    <div className="w-full max-w-[240px] mx-auto bg-white/5 rounded-lg border border-white/10 shadow-sm overflow-hidden">
      <div className="grid grid-cols-3 border-b border-white/10">
        <div className="px-2 py-1 text-[8px] font-semibold text-white border-r border-white/10 bg-white/[0.02]">Name</div>
        <div className="px-2 py-1 text-[8px] font-semibold text-white border-r border-white/10 bg-white/[0.02]">Email</div>
        <div className="px-2 py-1 text-[8px] font-semibold text-white bg-white/[0.02]">Tag</div>
      </div>
      <div className="grid grid-cols-3 border-b border-white/5">
        <div className="px-2 py-1 text-[8px] text-white/60 border-r border-white/5">Sarah M.</div>
        <div className="px-2 py-1 text-[8px] text-white/60 border-r border-white/5 truncate">sarah@...</div>
        <div className="px-2 py-1 text-[8px] text-white/60">Day</div>
      </div>
      <div className="grid grid-cols-3 border-b border-white/5">
        <div className="px-2 py-1 text-[8px] text-white/60 border-r border-white/5">John P.</div>
        <div className="px-2 py-1 text-[8px] text-white/60 border-r border-white/5 truncate">john@...</div>
        <div className="px-2 py-1 text-[8px] text-white/60">Eve</div>
      </div>
      <div className="px-2 py-1.5 flex items-center gap-2">
        <div className="px-2 py-0.5 rounded text-[8px] font-medium bg-primary/20 text-primary">Import</div>
        <div className="px-2 py-0.5 rounded text-[8px] font-medium bg-white/10 text-white/70">Export</div>
      </div>
    </div>
  ),
  // Full Customization
  (
    <div className="w-full max-w-[240px] mx-auto space-y-2">
      <div className="bg-white/5 rounded-lg border border-white/10 shadow-sm overflow-hidden">
        <div className="px-3 py-1.5 border-b border-white/10">
          <span className="text-[9px] text-white/70 font-medium">Color Palette</span>
        </div>
        <div className="px-2.5 py-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-[#8fa382] border border-white/10" />
            <span className="text-[10px] text-white">Sage Garden</span>
          </div>
          <ChevronDown size={10} className="text-white/40" />
        </div>
      </div>
      <div className="bg-white/5 rounded-lg border border-white/10 shadow-sm overflow-hidden">
        <div className="px-3 py-1.5 border-b border-white/10">
          <span className="text-[9px] text-white/70 font-medium">Font Pairing</span>
        </div>
        <div className="px-2.5 py-2 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-[10px] font-serif text-white leading-tight">Playfair Display</span>
            <span className="text-[8px] text-white/50">+ Lato</span>
          </div>
          <ChevronDown size={10} className="text-white/40" />
        </div>
      </div>
    </div>
  ),
];

const PLANNER_FEATURE_ICONS: ReactNode[] = [
  <Infinity className="w-5 h-5" />,
  <Check className="w-5 h-5" />,
  <EyeOff className="w-5 h-5" />,
  <UtensilsCrossed className="w-5 h-5" />,
  <FileSpreadsheet className="w-5 h-5" />,
  <Palette className="w-5 h-5" />,
];

export function WeddingPlannerSection({ strings }: { strings?: WeddingPlannerStrings }) {
  const s = strings ?? EN_DEFAULTS;

  return (
    <section className="py-20 md:py-32 bg-[#0a0a0a] overflow-hidden" id="planners">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-white mb-4"
            style={displayFont}
          >
            {s.wedding_planner_h2_pre}<span className="text-primary">{s.wedding_planner_h2_highlight}</span>{s.wedding_planner_h2_post}
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-base md:text-lg text-white/60 max-w-2xl mx-auto"
          >
            {s.wedding_planner_body}
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12"
        >
          {s.wedding_planner_features.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white/[0.03] rounded-xl border border-white/10 hover:shadow-lg hover:shadow-primary/5 transition-shadow overflow-hidden"
            >
              {PLANNER_FEATURE_VISUALS[index] && (
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 h-44 px-4 py-5 flex items-center justify-center">
                  {PLANNER_FEATURE_VISUALS[index]}
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 text-primary">
                    {PLANNER_FEATURE_ICONS[index]}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-white/50 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center"
        >
          <Link
            to="/create"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-medium"
          >
            {s.wedding_planner_cta}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
